<template>
    <div class="v-application v-application--is-ltr vuetify-wrapper">
        <section class="v-application align-center">
            <h3 class="mb-5">{{ isRestricted ? 'Steffi Anfragen' : 'Steffi Kunden' }}</h3>
            <steffi-offline-contract-dialog v-if="isRestricted" @saved="fetchCustomers()" />
            <steffi-offline-trial-dialog @saved="fetchCustomers()" />
        </section>
        <v-card class="v-application v-application--is-ltr">
            <v-card-text>
                <v-alert v-if="error" type="error" dense outlined>
                    Steffi Kunden konnten nicht geladen werden.
                </v-alert>

                <v-row class="v-application">
                    <v-col v-if="isRestricted" cols="auto" class="pt-1">
                        <v-chip-group
                            v-model="filters.queue"
                            mandatory
                            dense
                            color="primary"
                            data-test="steffi-queue-filter"
                        >
                            <v-chip value="open" data-test="steffi-queue-open">Offen</v-chip>
                            <v-chip value="all" data-test="steffi-queue-all">Alle</v-chip>
                        </v-chip-group>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field
                            v-model="filters.search"
                            append-icon="mdi-magnify"
                            label="Suche nach Apotheke, ID, Telefon"
                            data-test="steffi-customer-search"
                            clearable
                            hide-details
                            dense
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3" data-test="steffi-status-filter">
                        <v-select
                            v-model="filters.local_status"
                            :items="localStatusOptions"
                            label="FPP Status"
                            clearable
                            hide-details
                            dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="filters.famulor_status"
                            :items="famulorStatusOptions"
                            label="Technischer Status"
                            clearable
                            hide-details
                            dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="filters.plan_code"
                            :items="planOptions"
                            label="Paket"
                            clearable
                            hide-details
                            dense
                        ></v-select>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider class="mt-2"></v-divider>

            <v-data-table
                :headers="headers"
                :items="customers"
                :options.sync="tableOptions"
                :server-items-length="total"
                :loading="isLoading"
                class="v-application--is-ltr"
                mobile-breakpoint="900"
                loading-text="Wird geladen..."
                no-data-text="Keine Steffi Kunden gefunden"
                :footer-props="{
                    'items-per-page-options': [10, 25, 50, 100],
                    'show-first-last-page': true,
                    'show-current-page': true,
                    'items-per-page-text': 'Zeilen pro Seite'
                }"
                @update:options="onOptionsChange"
            >
                <template v-slot:item.pharmacy_customer_id="{ item }">
                    <span v-if="item.pharmacy_customer_id" class="text-caption">
                        {{ item.pharmacy_customer_id }}
                    </span>
                    <span v-else class="text--secondary">-</span>
                </template>

                <template v-slot:item.pharmacy_name="{ item }">
                    <a :href="pharmacyLink(item)" class="steffi-pharmacy-link" data-test="steffi-pharmacy-link">
                        {{ item.pharmacy_name }}
                    </a>
                </template>

                <template v-slot:item.callable_phone="{ item }">
                    <section class="numbers">
                        <div v-if="item.famulor_phone_number" class="v-application phone-container">
                            <a :href="`tel:${item.famulor_phone_number}`">
                                {{ item.famulor_phone_number }}
                            </a>
                            <span class="caption">(Steffi Nummer)</span>
                        </div>
                        <div v-if="item.pharmacy_phone_number" class="v-application phone-container">
                            <a :href="`tel:${item.pharmacy_phone_number}`">
                                {{ item.pharmacy_phone_number }}
                            </a>
                            <span class="caption">(Apothekenprofil)</span>
                        </div>
                    </section>
                </template>

                <template v-slot:item.local_subscription_status="{ item }">
                    <div class="v-application v-application--is-ltr">
                        <v-chip
                            small
                            :color="localStatusColor(item.local_subscription_status)"
                            :text-color="`${localStatusColor(item.local_subscription_status)} darken-2`"
                            class="lighten-4 font-weight-medium"
                        >
                            {{ localStatusLabel(item.local_subscription_status) }}
                        </v-chip>
                    </div>
                </template>

                <template v-slot:item.trial_status="{ item }">
                    <div v-if="item.trial_status" class="py-2" data-test="steffi-overview-trial-status">
                        <div class="font-weight-medium mb-1">
                            {{ item.trial_expired ? 'Testphase automatisch beendet' : trialStatusLabel(item.trial_status) }}
                        </div>
                        <div v-if="item.trial_started_at" class="text-no-wrap" data-test="steffi-trial-start">
                            <span class="text--secondary">Start:</span> {{ formatTrialDate(item.trial_started_at) }}
                        </div>
                        <div v-if="item.trial_ends_at" class="text-no-wrap" data-test="steffi-trial-end">
                            <span class="text--secondary">Ende:</span> {{ formatTrialDate(item.trial_ends_at) }}
                        </div>
                        <div v-if="item.trial_started_at || item.trial_ends_at" class="text-caption text--secondary">Berliner Zeit</div>
                    </div>
                    <span v-else class="text--secondary">-</span>
                </template>

                <template v-slot:item.famulor_operational_status="{ item }">
                    <div class="v-application v-application--is-ltr">
                        <v-chip
                            small
                            :color="famulorStatusColor(item.famulor_operational_status)"
                            :text-color="`${famulorStatusColor(item.famulor_operational_status)} darken-2`"
                            class="lighten-4 font-weight-medium"
                        >
                            {{ famulorStatusLabel(item.famulor_operational_status) }}
                        </v-chip>
                    </div>
                </template>

                <template v-slot:item.plan_code="{ item }">
                    <div class="bold">{{ item.plan_name || planLabel(item.plan_code) }}</div>
                </template>

                <template v-slot:item.technical_test_finished="{ item }">
                    <div class="v-application">
                        <v-checkbox
                            :input-value="item.technical_test_finished"
                            :loading="technicalTestSavingIds.includes(item.pharmacy_id)"
                            :disabled="!item.can_update_technical_test || technicalTestSavingIds.includes(item.pharmacy_id)"
                            hide-details
                            class="mt-0 pt-0"
                            :data-test="`${item.pharmacy_id}-steffi-technical-test-finished-item`"
                            @change="setTechnicalTestFinished(item, $event)"
                        ></v-checkbox>
                    </div>
                </template>

                <template v-slot:item.start_date="{ item }">
                    {{ formatDateOnly(item.start_date) || '-' }}
                </template>

                <template v-slot:item.requested_at="{ item }">
                    {{ formatDateOnly(item.requested_at) || '-' }}
                </template>

                <template v-slot:item.next_billing_date="{ item }">
                    {{ formatDateOnly(item.next_billing_date) || '-' }}
                </template>

                <template v-slot:item.open_invoice_count="{ item }">
                    <section class="counts">
                        <div>
                            <strong>{{ item.invoice_summary.open_count }}</strong> offen
                        </div>
                        <div class="text--secondary text-caption">
                            {{ item.invoice_summary.paid_count }} bezahlt
                        </div>
                        <div class="text--secondary text-caption">{{ item.invoice_summary.created_count }} vorbereitet</div>
                    </section>
                </template>

                <template v-slot:item.usage_total_minutes="{ item }">
                    <section data-test="steffi-usage" :data-pharmacy-id="item.pharmacy_id">
                        <div data-test="steffi-usage-sync" :data-state="item.usage_sync.state">
                            {{ usageSyncLabel(item.usage_sync.state) }}
                        </div>
                        <div v-if="!item.usage_sync.last_success_at && !['never_synced', 'not_applicable'].includes(item.usage_sync.state)" class="text-caption">
                            Nutzungsdaten noch nicht synchronisiert
                        </div>
                        <div v-if="item.usage_sync.last_success_at" class="text-caption">
                            Letzte erfolgreiche Synchronisierung: {{ formatTrialDate(item.usage_sync.last_success_at) }}
                        </div>
                        <div v-if="item.usage_sync.last_success_at || item.usage_summary.total_call_count" data-test="steffi-usage-counts">
                            <div v-if="item.usage_sync.state !== 'fresh'" class="text-caption">Bekannte Nutzung (unvollständig)</div>
                            <div>{{ item.usage_summary.total_call_count }} Calls</div>
                            <div class="text--secondary text-caption">
                                {{ item.usage_summary.total_billable_minutes }} Min.
                            </div>
                        </div>
                    </section>
                </template>

                <template v-slot:item.links="{ item }">
                    <section class="urls">
                        <v-tooltip v-for="invoice in item.invoices"
                            :key="invoice.invoice_id"
                            top
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <a
                                    v-bind="attrs"
                                    v-on="on"
                                    :href="`${apobilanzOriginUrl}/product-invoices/${invoice.id}`"
                                    target="_blank"
                                    rel="noopener"
                                    title="Rechnungen in Apobilanz"
                                    class="ml-1"
                                >
                                    <v-icon small>mdi-file-link-outline</v-icon>
                                </a>
                            </template>
                            <span>
                                {{ invoice.ref }}
                            </span>
                        </v-tooltip>

                        <div v-if="item.legal_acceptance && item.legal_acceptance.contract_url">
                            <a :href="item.legal_acceptance.contract_url" target="_blank" rel="noopener">Vertragsset öffnen</a>
                        </div>
                        <div v-if="item.legal_acceptance && item.legal_acceptance.agb_url">
                            <a :href="item.legal_acceptance.agb_url" target="_blank" rel="noopener">Nutzungsbedingungen öffnen</a>
                        </div>
                    </section>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
    import axios from 'axios';
    import _ from 'lodash';
    import { mapMutations } from 'vuex';
    import SteffiOfflineContractDialog from '../components/pharmacy/SteffiOfflineContractDialog.vue';
    import SteffiOfflineTrialDialog from '../components/pharmacy/SteffiOfflineTrialDialog.vue';

    export default {
        components: { SteffiOfflineContractDialog, SteffiOfflineTrialDialog },
        props: {
            accessScope: {
                type: String,
                default: 'admin',
            },
        },
        data() {
            const restricted = this.accessScope === 'steffi_team';
            return {
            customers: [],
            total: 0,
            isLoading: false,
            error: false,
            technicalTestSavingIds: [],
            resolvedAccessScope: this.accessScope,
            tableOptions: {
                page: 1,
                itemsPerPage: 25,
                sortBy: [restricted ? 'requested_at' : 'pharmacy_name'],
                sortDesc: [restricted],
            },
            filters: {
                queue: restricted ? 'open' : null,
                search: '',
                local_status: null,
                famulor_status: null,
                plan_code: null,
            },
            headers: [
                { text: 'K.-Nr.', value: 'pharmacy_customer_id' },
                { text: 'Apotheke', value: 'pharmacy_name' },
                { text: 'Telefon', value: 'callable_phone', sortable: false },
                { text: 'FPP Status', value: 'local_subscription_status' },
                { text: 'Testphase', value: 'trial_status', sortable: false },
                { text: 'Technischer Status', value: 'famulor_operational_status' },
                { text: 'Paket', value: 'plan_code' },
                { text: 'Start', value: 'start_date' },
                { text: 'Nächste Abrechnung', value: 'next_billing_date' },
                { text: 'Rechnungen', value: 'open_invoice_count' },
                { text: 'Nutzung', value: 'usage_total_minutes' },
                { text: '', value: 'links', sortable: false },
            ],
            localStatusOptions: [
                { text: 'Testphase', value: 'trial' },
                { text: 'Aktiv', value: 'active' },
                { text: 'Onboarding offen', value: 'pending_onboarding' },
                { text: 'Telefonnummer offen', value: 'awaiting_phone_number' },
                { text: 'Provisionierung', value: 'provisioning' },
                { text: 'Provisionierung fehlgeschlagen', value: 'provisioning_failed' },
                { text: 'Pausiert', value: 'paused' },
                { text: 'Gekündigt', value: 'cancelled' },
                { text: 'Historisch', value: 'historical' },
            ],
            famulorStatusOptions: [
                { text: 'Aktiv', value: 'active' },
                { text: 'Inaktiv', value: 'inactive' },
                { text: 'Unbekannt', value: 'unknown' },
            ],
            planOptions: [
                { text: 'Basis', value: 'base' },
                { text: 'Premium', value: 'premium' },
            ],
            };
        },
        computed: {
            isRestricted() {
                return this.resolvedAccessScope === 'steffi_team';
            },
            apobilanzOriginUrl() {
                const protocol = window.location.protocol;
                const adminHost = window.location.host;
                const apobilanzHost = adminHost.startsWith('admin.')
                    ? adminHost.replace(/^admin\./, 'apobilanz.')
                    : 'apobilanz.fp.local';

                return `${protocol}//${apobilanzHost}`;
            },
        },
        created() {
            if (this.isRestricted) {
                this.headers = [
                    { text: 'K.-Nr.', value: 'pharmacy_customer_id' },
                    { text: 'Apotheke', value: 'pharmacy_name' },
                    { text: 'Angefragt', value: 'requested_at' },
                    { text: 'Telefon', value: 'callable_phone', sortable: false },
                    { text: 'FPP Status', value: 'local_subscription_status' },
                    { text: 'Testphase', value: 'trial_status', sortable: false },
                    { text: 'Technischer Status', value: 'famulor_operational_status' },
                    { text: 'Paket', value: 'plan_code' },
                    { text: 'TechTest abgeschlossen', value: 'technical_test_finished', sortable: false },
                ];
            }
            this.fetchCustomersDebounced = _.debounce(() => {
                this.fetchCustomers({ page: 1 });
            }, 250);
        },
        mounted() {
            this.fetchCustomers();
        },
        watch: {
            filters: {
                deep: true,
                handler() {
                    this.fetchCustomersDebounced();
                }
            }
        },
        methods: {
            usageSyncLabel(state) {
                return {
                    not_applicable: 'Keine nutzungsrelevante Buchung.',
                    never_synced: 'Nutzungsdaten noch nicht synchronisiert',
                    fresh: 'Nutzungsdaten aktuell',
                    stale: 'Nutzungsdaten veraltet',
                    failed: 'Synchronisierung fehlgeschlagen',
                }[state];
            },
            trialStatusLabel(status) {
                return { requested: 'Testphase angefragt', running: 'Testphase läuft', cancelled: 'Testphase beendet', expired: 'Testphase automatisch beendet', converted: 'Testphase abgeschlossen' }[status] || status;
            },
            async fetchCustomers(overrides = {}) {
                const options = {
                    ...this.tableOptions,
                    ...overrides,
                };
                const sortBy = options.sortBy && options.sortBy[0] ? options.sortBy[0] : 'pharmacy_name';
                const sortAsc = options.sortDesc && options.sortDesc[0] ? -1 : 1;

                try {
                    this.isLoading = true;
                    this.error = false;
                    const response = await axios.get('/api/steffi/customers', {
                        params: {
                            page: options.page || 1,
                            page_size: options.itemsPerPage || 10,
                            sort_by: sortBy,
                            sort_asc: sortAsc,
                            ...this.activeFilters(),
                        }
                    });
                    this.customers = response.data.items || [];
                    this.total = response.data.total || 0;
                    this.resolvedAccessScope = response.data.access_scope || this.resolvedAccessScope;
                    this.tableOptions = {
                        ...this.tableOptions,
                        page: response.data.page || options.page || 1,
                        itemsPerPage: response.data.page_size || options.itemsPerPage || 10,
                    };
                } catch (error) {
                    this.error = true;
                    this.alertError();
                    console.log('failed to fetch Steffi customers', error);
                } finally {
                    this.isLoading = false;
                }
            },
            async setTechnicalTestFinished(item, value) {
                if (!item?.pharmacy_id || this.technicalTestSavingIds.includes(item.pharmacy_id)) return;
                this.technicalTestSavingIds.push(item.pharmacy_id);
                try {
                    await axios.put(
                        `/api/pharmacies/${item.pharmacy_id}/steffi/manual-setup`,
                        { technical_test_finished: Boolean(value) },
                    );
                    await this.fetchCustomers();
                    this.alertSuccess('TechTest-Status gespeichert');
                } catch (error) {
                    this.alertError(error?.response?.data?.message || 'TechTest-Status konnte nicht gespeichert werden.');
                } finally {
                    this.technicalTestSavingIds = this.technicalTestSavingIds.filter(id => id !== item.pharmacy_id);
                }
            },
            activeFilters() {
                const params = {};
                Object.entries(this.filters).forEach(([key, value]) => {
                    if (value === null || value === undefined || value === '') return;
                    params[key] = value;
                });
                return params;
            },
            onOptionsChange(options) {
                this.fetchCustomers(options);
            },
            formatDateOnly(date) {
                if (!date) return null;
                return this.$formatDate(date, 'DD.MM.YYYY');
            },
            formatTrialDate(date) {
                return new Date(date).toLocaleString('de-DE', {
                    timeZone: 'Europe/Berlin',
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                });
            },
            localStatusLabel(status) {
                return {
                    active: 'Aktiv',
                    pending_onboarding: 'Onboarding offen',
                    awaiting_phone_number: 'Telefonnummer offen',
                    provisioning: 'Provisionierung',
                    provisioning_failed: 'Fehler',
                    paused: 'Pausiert',
                    cancelled: 'Gekündigt',
                    historical: 'Historisch',
                }[status] || status || 'Unbekannt';
            },
            localStatusColor(status) {
                return {
                    active: 'green',
                    pending_onboarding: 'blue',
                    awaiting_phone_number: 'orange',
                    provisioning: 'blue',
                    provisioning_failed: 'red',
                    paused: 'orange',
                    cancelled: 'blue-grey',
                    historical: 'grey',
                }[status] || 'grey';
            },
            famulorStatusLabel(status) {
                return {
                    active: 'Aktiv',
                    inactive: 'Inaktiv',
                    unknown: 'Unbekannt',
                }[status] || 'Unbekannt';
            },
            famulorStatusColor(status) {
                return {
                    active: 'green',
                    inactive: 'red',
                    unknown: 'grey',
                }[status] || 'grey';
            },
            planLabel(planCode) {
                return {
                    base: 'Basis',
                    premium: 'Premium',
                }[planCode] || planCode || '-';
            },
            pharmacyLink(item) {
                return item.links?.steffi_team_pharmacy || item.links?.pharmacy_profile || '#';
            },
            productInvoiceBucket(invoiceSummary = {}) {
                const buckets = [
                    ['open', 'open_count'],
                    ['created', 'created_count'],
                    ['paid', 'paid_count'],
                ];
                const match = buckets.find(([, countKey]) => Number(invoiceSummary[countKey] || 0) > 0);
                return match ? match[0] : 'open';
            },
            ...mapMutations({
                alertSuccess: 'alert/success',
                alertError: 'alert/error',
            })
        }
    }
</script>

<style scoped>
.phone-container {
    display: block;
    margin-top: 0.5rem;
}
.bold {
    font-weight: bold;
}
.counts, .numbers {
    min-width: 7rem;
}
.urls {
    text-align: center;
}
::v-deep th {
  background-color: #f2f2f2 !important;
}
</style>
